import React, { useEffect, useState } from 'react';
import SEOHead from '../../../components/SEOHead';
import HormuzHubNav from '../../../components/HormuzHubNav';
import { chokepointsAPI } from '../../../services/api';
import {
  getOrganizationSchema,
  getBreadcrumbSchema,
  getDatasetSchema,
  getMonitoringServiceSchema,
} from '../../../utils/seoSchemas';
import { AlertTriangle, Activity, TrendingUp, Shield, Zap } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

const HormuzRiskDashboard = () => {
  const [riskData, setRiskData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await chokepointsAPI.getRiskScore('hormuz');
        setRiskData(res.data);
      } catch (e) {
        console.error('Failed to load risk score', e);
      } finally {
        setLoading(false);
      }
    };
    load();
    // Auto-refresh every 5 minutes
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!riskData) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-slate-500 dark:text-on-surface-variant">Risk data unavailable.</p>
      </div>
    );
  }

  const { scores, risk_level, explanation, calculated_at } = riskData;

  const chartData = [
    { name: 'Military', value: Number(scores.military_incident) || 50, weight: '30%' },
    { name: 'Oil Volatility', value: Number(scores.oil_volatility) || 50, weight: '25%' },
    { name: 'Freight', value: Number(scores.freight) || 50, weight: '20%' },
    { name: 'Insurance', value: Number(scores.insurance) || 50, weight: '15%' },
    { name: 'Sentiment', value: Number(scores.sentiment) || 50, weight: '10%' },
  ];

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return '#10b981';
      case 'moderate': return '#f59e0b';
      case 'high': return '#f97316';
      case 'critical': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getBarColor = (value) => {
    if (value < 30) return '#10b981';
    if (value < 60) return '#f59e0b';
    if (value < 80) return '#f97316';
    return '#ef4444';
  };

  const title = 'Strait of Hormuz Risk Dashboard | Live Risk Score';
  const compositeNum = Number(scores.composite) || 50;
  const description = `Current composite risk score: ${compositeNum.toFixed(1)}/100 (${risk_level}). Real-time monitoring of military incidents, oil volatility, freight disruption, insurance risk, and news sentiment for the Strait of Hormuz.`;

  const jsonLd = [
    getOrganizationSchema(),
    getBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Strait of Hormuz', path: '/strait-of-hormuz' },
      { name: 'Risk Dashboard', path: '/strait-of-hormuz/risk-dashboard' },
    ]),
    getDatasetSchema(
      'Strait of Hormuz Risk Scores',
      description,
      {
        path: '/strait-of-hormuz/risk-dashboard',
        keywords: ['risk score', 'risk assessment', 'monitoring', 'composite risk'],
        variableMeasured: [
          `Composite Risk: ${compositeNum.toFixed(1)}/100`,
          `Military Incidents: ${Number(scores.military_incident) || 50}/100`,
          `Oil Volatility: ${Number(scores.oil_volatility) || 50}/100`,
          `Freight: ${Number(scores.freight) || 50}/100`,
          `Insurance: ${Number(scores.insurance) || 50}/100`,
          `Sentiment: ${Number(scores.sentiment) || 50}/100`,
        ],
      }
    ),
    getMonitoringServiceSchema(
      'Strait of Hormuz Risk Monitoring',
      description,
      'https://traderadarglobal.com/strait-of-hormuz/risk-dashboard'
    ),
  ];

  return (
    <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title={title}
        description={description}
        path="/strait-of-hormuz/risk-dashboard"
        keywords="Hormuz risk score, Strait of Hormuz risk today, Hormuz risk assessment, oil chokepoint risk"
        jsonLd={jsonLd}
        image={`/api/og-image/hormuz-risk-${Math.round(compositeNum)}.png`}
        imageAlt={`Strait of Hormuz Risk Dashboard - Current risk score: ${compositeNum.toFixed(1)}/100`}
      />

      <HormuzHubNav />

      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-2">
          Strait of Hormuz Risk Dashboard
        </h1>
        <p className="text-slate-600 dark:text-on-surface-variant">
          Real-time composite risk assessment updated {new Date(calculated_at).toLocaleString()}
        </p>
      </header>

      {/* Composite Score Card */}
      <div className="mb-8">
        <div
          className="rounded-lg p-8 border-2"
          style={{ borderColor: getRiskColor(risk_level) }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-slate-600 dark:text-on-surface-variant mb-1">Composite Risk Score</p>
            <p className="text-5xl font-bold" style={{ color: getRiskColor(risk_level) }}>
              {(Number(scores.composite) || 50).toFixed(1)}
            </p>
              <p className="text-sm text-slate-500 dark:text-outline mt-1">out of 100</p>
            </div>
            <div className="text-right">
              <span
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: `${getRiskColor(risk_level)}20`,
                  color: getRiskColor(risk_level),
                }}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                {risk_level.toUpperCase()} RISK
              </span>
            </div>
          </div>
          {explanation && (
            <p className="text-sm text-slate-700 dark:text-on-surface-variant mt-4 border-t pt-4">
              {explanation}
            </p>
          )}
        </div>
      </div>

      {/* Component Scores Chart */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-4">
          Component Risk Scores
        </h2>
        <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 dark:border-outline-variant/15 dark:bg-surface-container">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" name="Risk Score">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry.value)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Component Score Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <ScoreCard
          label="Military Incidents"
          value={Number(scores.military_incident) || 50}
          weight="30%"
          icon={Shield}
          color={getBarColor(Number(scores.military_incident) || 50)}
        />
        <ScoreCard
          label="Oil Volatility"
          value={Number(scores.oil_volatility) || 50}
          weight="25%"
          icon={TrendingUp}
          color={getBarColor(Number(scores.oil_volatility) || 50)}
        />
        <ScoreCard
          label="Freight Disruption"
          value={Number(scores.freight) || 50}
          weight="20%"
          icon={Activity}
          color={getBarColor(Number(scores.freight) || 50)}
        />
        <ScoreCard
          label="Insurance Risk"
          value={Number(scores.insurance) || 50}
          weight="15%"
          icon={AlertTriangle}
          color={getBarColor(Number(scores.insurance) || 50)}
        />
        <ScoreCard
          label="News Sentiment"
          value={Number(scores.sentiment) || 50}
          weight="10%"
          icon={Zap}
          color={getBarColor(Number(scores.sentiment) || 50)}
        />
      </div>

      {/* Risk Level Explanation */}
      <div className="bg-slate-100 dark:bg-surface-container-low rounded-lg p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-3">
          Risk Level Interpretation
        </h3>
        <div className="space-y-2 text-sm text-slate-700 dark:text-on-surface-variant">
          <p>
            <strong>Low (0-30):</strong> Minimal disruption risk. Normal operations expected.
          </p>
          <p>
            <strong>Moderate (31-60):</strong> Some elevated risk factors. Monitor closely.
          </p>
          <p>
            <strong>High (61-80):</strong> Significant risk factors present. Prepare for potential disruptions.
          </p>
          <p>
            <strong>Critical (81-100):</strong> Severe risk conditions. Immediate impact on oil flows likely.
          </p>
        </div>
      </div>
    </article>
  );
};

const ScoreCard = ({ label, value, weight, icon: Icon, color }) => {
  const numValue = Number(value) || 50;
  return (
    <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-4 dark:border-outline-variant/15 dark:bg-surface-container">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          {Icon && <Icon className="w-4 h-4" style={{ color }} />}
          <span className="text-sm font-medium text-slate-700 dark:text-on-surface-variant">{label}</span>
        </div>
        <span className="text-xs text-slate-500 dark:text-on-surface-variant">Weight: {weight}</span>
      </div>
      <p className="text-2xl font-bold" style={{ color }}>
        {numValue.toFixed(1)}
      </p>
      <div className="mt-2 w-full bg-gray-200 dark:bg-surface-container-low rounded-full h-2">
        <div
          className="h-2 rounded-full"
          style={{ width: `${numValue}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

export default HormuzRiskDashboard;

